<script>
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    issueState,
    addIssue,
    updateIssue as updateIssueInStore,
    removeIssue as removeIssueFromStore,
} from "@app/store/issueStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { saveAs } from "file-saver";
import { unparse } from "papaparse";
import { toast } from "@zerodevx/svelte-toast";
import { lockState } from "@app/store/lockStore.svelte.js";
import _Issue from "@cclib/classes/issue.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Event from "@cclib/classes/event.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import * as m from "@app/paraglide/messages.js";

// UI State
let isCreating = $state(false);
let isEditing = $state(false);
let selectedIssue = $state(null);
let searchQuery = $state("");
let selectedFilter = $state("all");
let selectedType = $state("all");
let showFilters = $state(false);

// Form State
let issueName = $state("");
let issueType = $state("note");
let issueDescription = $state("");
let issuePriority = $state("medium");
let issueStatus = $state("open");
let isEventRelated = $state(false);

// Enhanced type and priority options
const issueTypes = [
    { value: "note", label: m.manual_qc_type_note(), icon: "📝" },
    { value: "technical", label: m.manual_qc_type_technical(), icon: "⚙️" },
    { value: "content", label: m.manual_qc_type_content(), icon: "📄" },
    { value: "grammar", label: m.manual_qc_type_grammar(), icon: "✏️" },
    { value: "feedback", label: m.manual_qc_type_feedback(), icon: "💬" },
    { value: "timing", label: m.manual_qc_type_timing(), icon: "⏱️" },
    { value: "other", label: m.manual_qc_type_other(), icon: "🔖" }
];

const priorityOptions = [
    { value: "low", label: m.manual_qc_priority_low(), color: "#28a745" },
    { value: "medium", label: m.manual_qc_priority_medium(), color: "#ffc107" },
    { value: "high", label: m.manual_qc_priority_high(), color: "#fd7e14" },
    { value: "critical", label: m.manual_qc_priority_critical(), color: "#dc3545" }
];

const statusOptions = [
    { value: "open", label: m.manual_qc_status_open(), color: "#17a2b8" },
    { value: "in-progress", label: m.manual_qc_status_in_progress(), color: "#ffc107" },
    { value: "review", label: m.manual_qc_status_review(), color: "#6f42c1" },
    { value: "resolved", label: m.manual_qc_status_resolved(), color: "#28a745" },
    { value: "closed", label: m.manual_qc_status_closed(), color: "#6c757d" }
];

// Computed values
let eventsSelected = $derived(
    (() => {
        const eventGroup = eventGroupState[projectState.selected];
        if (!eventGroup) return [];
        return eventGroup.selected
            .map((index) => eventGroup.events[index])
            .filter((event) => event !== undefined);
    })()
);

let selectedEventGroup = $derived(eventGroupState[projectState.selected]);

let filteredIssues = $derived(
    issueState.value
        .filter((issue) => {
            const matchesSearch = !searchQuery || 
                issue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                issue.description.toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesFilter = selectedFilter === "all" || issue.status === selectedFilter;
            const matchesType = selectedType === "all" || issue.type === selectedType;
            
            return matchesSearch && matchesFilter && matchesType;
        })
        .sort((a, b) => {
            // Sort by priority (critical > high > medium > low), then by creation date
            const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
            const priorityDiff = (priorityOrder[b.priority] || 2) - (priorityOrder[a.priority] || 2);
            if (priorityDiff !== 0) return priorityDiff;
            
            // Then by creation date (newest first)
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        })
);

// Utility functions
function resetForm() {
    issueName = "";
    issueType = "note";
    issueDescription = "";
    issuePriority = "medium";
    issueStatus = "open";
    isEventRelated = false;
    selectedIssue = null;
    isCreating = false;
    isEditing = false;
}

function startCreating() {
    resetForm();
    isCreating = true;
    // Auto-detect if events are selected
    if (eventsSelected.length > 0) {
        isEventRelated = true;
    }
}

function startEditing(issue) {
    selectedIssue = issue;
    issueName = issue.name || "";
    issueType = issue.type || "note";
    issueDescription = issue.description || "";
    issuePriority = issue.priority || "medium";
    issueStatus = issue.status || "open";
    isEventRelated = issue.eventIds && issue.eventIds.length > 0;
    isEditing = true;
    isCreating = false;
}

function cancelEditing() {
    resetForm();
}

function createIssue() {
    if (!issueName.trim()) {
        toast.push(m.manual_qc_error_name_required(), { classes: ["toast-error"] });
        return;
    }

    let eventIdsSelected = [];
    if (isEventRelated && eventsSelected.length > 0) {
        eventIdsSelected = eventsSelected.map(event => event.id);
    }

    const newIssue = {
        ...new _Issue({
            name: issueName.trim(),
            priority: issuePriority,
            status: issueStatus,
            type: issueType,
            description: issueDescription.trim(),
            eventGroupId: isEventRelated ? selectedEventGroup?.id : null,
            eventIds: eventIdsSelected,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }),
    };

    addIssue(newIssue);
    toast.push(m.manual_qc_created_success(), { classes: ["toast-success"] });
    resetForm();
    return newIssue;
}

function updateIssue() {
    if (!selectedIssue || !selectedIssue.id) {
        console.error("No valid issue selected for update");
        return;
    }

    if (!issueName.trim()) {
        toast.push(m.manual_qc_error_name_required(), { classes: ["toast-error"] });
        return;
    }

    const updatedData = {
        name: issueName.trim(),
        priority: issuePriority,
        status: issueStatus,
        type: issueType,
        description: issueDescription.trim(),
        updatedAt: new Date().toISOString()
    };

    updateIssueInStore(selectedIssue.id, updatedData);
    toast.push(m.manual_qc_updated_success(), { classes: ["toast-success"] });
    resetForm();
}

function removeIssue(issue) {
    Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.destructive,
        title: m.manual_qc_delete_title(),
        text: m.manual_qc_delete_confirm({ name: issue.name }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: m.manual_qc_delete_button(),
        cancelButtonText: m.manual_qc_cancel_button(),
    }).then((result) => {
        if (result.isConfirmed) {
            removeIssueFromStore(issue.id);
            toast.push(m.manual_qc_deleted_success(), { classes: ["toast-success"] });
            if (selectedIssue?.id === issue.id) {
                resetForm();
            }
        }
    });
}

function openRelatedEvents(issue) {
    if (!issue.eventIds || issue.eventIds.length === 0) {
        toast.push(m.manual_qc_no_related_events(), { classes: ["toast-info"] });
        return;
    }

    // Find the event group
    let matchingEventGroup = null;
    let matchingEventGroupIndex = -1;

    for (let i = 0; i < eventGroupState.length; i++) {
        if (eventGroupState[i] && eventGroupState[i].id === issue.eventGroupId) {
            matchingEventGroup = eventGroupState[i];
            matchingEventGroupIndex = i;
            break;
        }
    }

    if (!matchingEventGroup) {
        toast.push(m.manual_qc_related_group_not_found(), { classes: ["toast-error"] });
        return;
    }

    // Switch to that event group and select the events
    projectState.selected = matchingEventGroupIndex;
    
    const eventIndices = issue.eventIds
        .map((eventId) => {
            // console.log(eventId);
            const index = matchingEventGroup.events.findIndex(
                (event) => event.id === eventId
            );

            return index;
        })
        .filter((index) => index !== -1);

    if (eventIndices.length > 0) {
        eventGroupState[projectState.selected].selected = eventIndices;
        
        // Scroll to first event if possible
        const firstEventIndex = eventIndices[0];
        if (lockState.video && 
            eventGroupState[projectState.selected].events[firstEventIndex] &&
            eventGroupState[projectState.selected].events[firstEventIndex].start >= 0) {
            if (typeof player !== 'undefined') {
                player.currentTime = eventGroupState[projectState.selected].events[firstEventIndex].start;
            }
        }

        // Dispatch scroll event
        window.dispatchEvent(
            new CustomEvent("scrollToVirtualListItem", {
                detail: { index: firstEventIndex },
            })
        );
    }
}

function exportIssues() {
    if (filteredIssues.length === 0) {
        toast.push(m.manual_qc_no_issues_export(), { classes: ["toast-info"] });
        return;
    }

    const issuesToExport = filteredIssues.map(issue => ({
        [m.manual_qc_export_name()]: issue.name,
        [m.manual_qc_export_type()]: issue.type,
        [m.manual_qc_export_priority()]: issue.priority,
        [m.manual_qc_export_status()]: issue.status,
        [m.manual_qc_export_description()]: issue.description,
        [m.manual_qc_export_created_at()]: issue.createdAt ? new Date(issue.createdAt).toLocaleString() : "",
        [m.manual_qc_export_updated_at()]: issue.updatedAt ? new Date(issue.updatedAt).toLocaleString() : "",
        [m.manual_qc_export_event_related()]: issue.eventIds?.length > 0 ? m.manual_qc_yes() : m.manual_qc_no()
    }));

    const csv = unparse(issuesToExport, { header: true });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });

    Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.confirmCancel,
        title: m.manual_qc_export_title(),
        showCancelButton: true,
        confirmButtonText: m.manual_qc_export_button(),
        input: "text",
        inputLabel: m.manual_qc_filename_label(),
        inputPlaceholder: m.manual_qc_filename_placeholder(),
        inputValue: `issues-${new Date().toISOString().split('T')[0]}`,
    }).then((result) => {
        if (result.isConfirmed && result.value) {
            saveAs(blob, result.value + ".csv");
        }
    });
}

function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getTypeIcon(type) {
    return issueTypes.find(t => t.value === type)?.icon || "🔖";
}

function getPriorityColor(priority) {
    return priorityOptions.find(p => p.value === priority)?.color || "#6c757d";
}

function getStatusColor(status) {
    return statusOptions.find(s => s.value === status)?.color || "#6c757d";
}

function getPriorityLabel(priority) {
    return priorityOptions.find(p => p.value === priority)?.label || priority;
}

function getStatusLabel(status) {
    return statusOptions.find(s => s.value === status)?.label || status;
}
</script>

<div class="manual-qc-panel resize-vertical">
    <!-- Header -->
    <div class="panel-header border-bottom">
        <h5 class="panel-title text-dark">
            <i class="bi bi-clipboard-check"></i>
            {m.manual_qc_title()}
        </h5>
        <div class="header-actions">
            <button
                aria-label={m.manual_qc_toggle_filters_label()}
                class="btn btn-sm btn-outline-secondary"
                onclick={() => showFilters = !showFilters}
                title={m.manual_qc_toggle_filters_label()}>
                <i class="bi bi-funnel"></i>
            </button>
            <button
                aria-label={m.manual_qc_export_label()}
                class="btn btn-sm btn-outline-secondary"
                onclick={exportIssues}
                title={m.manual_qc_export_label()}>
                <i class="bi bi-download"></i>
            </button>
            <button
                aria-label={m.manual_qc_add_issue_label()}
                class="btn btn-sm btn-primary"
                onclick={startCreating}
                title={m.manual_qc_add_issue_label()}>
                <i class="bi bi-plus-lg"></i>
            </button>
        </div>
    </div>

    <!-- Filters (collapsible) -->
    {#if showFilters}
        <div class="filters-panel">
            <div class="row g-2">
                <div class="col-md-4">
                    <input
                        type="text"
                        class="form-control form-control-sm"
                        placeholder={m.manual_qc_search_placeholder()}
                        bind:value={searchQuery}
                        aria-label={m.manual_qc_search_label()}>
                </div>
                <div class="col-md-4">
                    <select
                        class="form-select form-select-sm"
                        bind:value={selectedFilter}
                        aria-label={m.manual_qc_filter_status_label()}>
                        <option value="all">{m.manual_qc_filter_status_all()}</option>
                        {#each statusOptions as status}
                            <option value={status.value}>{status.label}</option>
                        {/each}
                    </select>
                </div>
                <div class="col-md-4">
                    <select
                        class="form-select form-select-sm"
                        bind:value={selectedType}
                        aria-label={m.manual_qc_filter_type_label()}>
                        <option value="all">{m.manual_qc_filter_type_all()}</option>
                        {#each issueTypes as type}
                            <option value={type.value}>{type.icon} {type.label}</option>
                        {/each}
                    </select>
                </div>
            </div>
        </div>
    {/if}

    <!-- Create/Edit Form -->
    {#if isCreating || isEditing}
        <div class="issue-form bg-light text-dark shadow rounded">
            <div class="form-header">
                <h6>{isEditing ? m.manual_qc_edit_title() : m.manual_qc_create_title()}</h6>
                <button
                    aria-label={m.manual_qc_cancel_edit_label()}
                    class="btn btn-sm btn-outline-secondary"
                    onclick={cancelEditing}
                    title={m.manual_qc_cancel_button()}>
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>

            <div class="row g-2 mb-2">
                <div class="col-md-8">
                    <input
                        type="text"
                        class="form-control form-control-sm"
                        placeholder={m.manual_qc_issue_title_placeholder()}
                        bind:value={issueName}
                        aria-label={m.manual_qc_issue_name_label()}>
                </div>
                <div class="col-md-4">
                    <select
                        class="form-select form-select-sm"
                        bind:value={issueType}
                        aria-label={m.manual_qc_issue_type_label()}>
                        {#each issueTypes as type}
                            <option value={type.value}>{type.icon} {type.label}</option>
                        {/each}
                    </select>
                </div>
            </div>

            <div class="row g-2 mb-2">
                <div class="col-md-4">
                    <select
                        class="form-select form-select-sm"
                        bind:value={issuePriority}
                        aria-label={m.manual_qc_priority_label()}>
                        {#each priorityOptions as priority}
                            <option value={priority.value}>{priority.label}</option>
                        {/each}
                    </select>
                </div>
                <div class="col-md-4">
                    <select
                        class="form-select form-select-sm"
                        bind:value={issueStatus}
                        aria-label={m.manual_qc_status_label()}>
                        {#each statusOptions as status}
                            <option value={status.value}>{status.label}</option>
                        {/each}
                    </select>
                </div>
                <div class="col-md-4">
                    <div class="form-check mt-1">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            bind:checked={isEventRelated}
                            disabled={!eventsSelected.length}
                            id="eventRelated">
                        <label class="form-check-label" for="eventRelated">
                            {m.manual_qc_event_related_label()}
                        </label>
                    </div>
                </div>
            </div>

            <div class="mb-2">
                <textarea
                    class="form-control form-control-sm"
                    rows="3"
                    placeholder={m.manual_qc_description_placeholder()}
                    bind:value={issueDescription}
                    aria-label={m.manual_qc_description_label()}></textarea>
            </div>

            {#if isEventRelated && eventsSelected.length > 0}
                <div class="event-info mb-2">
                    <small class="text-muted">
                        <i class="bi bi-link-45deg"></i>
                        {m.manual_qc_linked_events({ count: eventsSelected.length })}
                    </small>
                </div>
            {/if}

            <div class="form-actions">
                {#if isCreating}
                    <button
                        class="btn btn-primary btn-sm"
                        onclick={createIssue}
                        disabled={!issueName.trim()}>
                        <i class="bi bi-plus-lg"></i> {m.manual_qc_create_button()}
                    </button>
                {:else}
                    <button
                        class="btn btn-warning btn-sm"
                        onclick={updateIssue}
                        disabled={!issueName.trim()}>
                        <i class="bi bi-pencil"></i> {m.manual_qc_update_button()}
                    </button>
                {/if}
                <button
                    class="btn btn-outline-secondary btn-sm"
                    onclick={cancelEditing}>
                    {m.manual_qc_cancel_button()}
                </button>
            </div>
        </div>
    {/if}

    <!-- Issues List -->
    <div class="issues-list">
        {#if filteredIssues.length === 0}
            <div class="empty-state">
                <i class="bi bi-clipboard-x"></i>
                <p>{m.manual_qc_empty_state_title()}</p>
                <small class="text-muted">
                    {searchQuery ? m.manual_qc_empty_state_hint_search() : m.manual_qc_empty_state_hint_create()}
                </small>
            </div>
        {:else}
            {#each filteredIssues as issue (issue.id)}
                <div class="issue-card bg-light">
                    <div class="issue-header">
                        <div class="issue-title">
                            <span class="issue-type">{getTypeIcon(issue.type)}</span>
                            <span class="issue-name">{issue.name}</span>
                        </div>
                        <div class="issue-actions">
                            {#if issue.eventIds && issue.eventIds.length > 0}
                                <button
                                    aria-label={m.manual_qc_view_related_label()}
                                    class="btn btn-sm btn-outline-primary"
                                    onclick={() => openRelatedEvents(issue)}
                                    title={m.manual_qc_view_related_label()}>
                                    <i class="bi bi-eye"></i>
                                </button>
                            {/if}
                            <button
                                aria-label={m.manual_qc_edit_label()}
                                class="btn btn-sm btn-outline-secondary"
                                onclick={() => startEditing(issue)}
                                title={m.manual_qc_edit_label()}>
                                <i class="bi bi-pencil"></i>
                            </button>
                            <button
                                aria-label={m.manual_qc_delete_label()}
                                class="btn btn-sm btn-outline-danger"
                                onclick={() => removeIssue(issue)}
                                title={m.manual_qc_delete_label()}>
                                <i class="bi bi-trash"></i>
                            </button>
                        </div>
                    </div>

                    <div class="issue-meta">
                        <span class="badge" style="background-color: {getPriorityColor(issue.priority)}">
                            {getPriorityLabel(issue.priority)}
                        </span>
                        <span class="badge" style="background-color: {getStatusColor(issue.status)}">
                            {getStatusLabel(issue.status)}
                        </span>
                        {#if issue.eventIds && issue.eventIds.length > 0}
                            <span class="badge bg-secondary">
                                <i class="bi bi-link-45deg"></i> {m.manual_qc_event_count_badge({ count: issue.eventIds.length })}
                            </span>
                        {/if}
                    </div>

                    {#if issue.description}
                        <div class="issue-description">
                            {issue.description}
                        </div>
                    {/if}

                    <div class="issue-footer">
                        <small class="text-muted">
                            {m.manual_qc_created_label()}: {formatDate(issue.createdAt)}
                            {#if issue.updatedAt && issue.updatedAt !== issue.createdAt}
                                • {m.manual_qc_updated_label()}: {formatDate(issue.updatedAt)}
                            {/if}
                        </small>
                    </div>
                </div>
            {/each}
        {/if}
    </div>
</div>

<style>
.manual-qc-panel {
    height: 300px;
    display: flex;
    flex-direction: column;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
}

.panel-title {
    margin: 0;
    font-weight: 600;
}

.header-actions {
    display: flex;
    gap: 0.5rem;
}

.filters-panel {
    padding: 1rem;
}

.issue-form {
    margin: 1rem;
    padding: 1rem;
}

.form-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.form-header h6 {
    margin: 0;
}

.form-actions {
    display: flex;
    gap: 0.5rem;
}

.event-info {
    padding: 0.5rem;
    border-radius: 4px;
}

.issues-list {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
}

.empty-state {
    text-align: center;
    padding: 3rem 1rem;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
}

.issue-card {
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    transition: all 0.2s ease;
}

.issue-card:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    transform: translateY(-1px);
}

.issue-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
}

.issue-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
}

.issue-type {
    font-size: 1.2rem;
}

.issue-name {
    font-weight: 600;
}

.issue-actions {
    display: flex;
    gap: 0.25rem;
}

.issue-meta {
    margin-bottom: 0.5rem;
}

.issue-meta .badge {
    margin-right: 0.5rem;
    font-size: 0.75rem;
}

.issue-description {
    margin-bottom: 0.5rem;
    line-height: 1.4;
}

.issue-footer {
    border-top: 1px solid #e9ecef;
    padding-top: 0.5rem;
}

@media (max-width: 768px) {
    .issue-header {
        flex-direction: column;
        gap: 0.5rem;
    }
    
    .issue-actions {
        align-self: flex-end;
    }
}
</style>